import type {
  NotificationCategory,
  NotificationCategoryToggles,
  NotificationEventType,
  NotificationTier,
} from '../types/schema'

export const SUPPRESSED_EVENT_TYPES: ReadonlySet<string> = new Set([
  'AD_LOCK_READY',
  'AD_UNLOCK',
  'UNLOCK_READY',
])

export const HIGH_CATEGORIES: ReadonlySet<NotificationCategory> = new Set<NotificationCategory>([
  'MENTIONS',
  'DUE',
  'AI_FAILS',
  'SUBSCRIPTION',
  'SECURITY',
  'EDU_ATTENDANCE',
  'EDU_LEAVE',
  'EDU_CLASSES',
  'EDU_REPORTS',
  'EDU_FEES',
  'EDU_BOOKINGS',
  'EDU_PROGRESS',
  'EDU_MESSAGES',
  'EDU_ADMISSIONS',
  'EDU_EVENTS',
])

export const EVENT_CATEGORY_MAP: Partial<Record<NotificationEventType, NotificationCategory>> = {
  MENTION_CREATED: 'MENTIONS',
  ASSIGNMENT_CREATED: 'MENTIONS',
  ASSIGNMENT_CHANGED: 'UPDATES',
  DUE_SOON: 'DUE',
  DUE_TODAY: 'DUE',
  CHECKLIST_DUE: 'DUE',
  OVERDUE: 'DUE',
  MISSED: 'DUE',
  CHECKLIST_MISSED: 'DUE',
  WINDOW_ENDING: 'DUE',
  CHECKLIST_WINDOW_ENDING: 'DUE',
  CHECKLIST_SUMMARY: 'DUE',
  BLOCKED: 'DUE',
  UNBLOCKED: 'DUE',
  RELOCKED: 'DUE',
  PROOF_SUBMITTED: 'DUE',
  PROOF_APPROVED: 'DUE',
  PROOF_REJECTED: 'DUE',
  EXPENSE_APPROVED: 'DUE',
  EXPENSE_REJECTED: 'DUE',
  EDU_ANNOUNCEMENT_WHATSAPP: 'DUE',
  WORKFLOW_STATUS_CHANGED: 'UPDATES',
  COMPLETION_REVIEW_REQUIRED: 'DUE',
  RAID_STARTED: 'GAMIFICATION',
  RAID_ENRAGE_WARNING: 'DUE',
  RAID_COMPLETED: 'GAMIFICATION',
  RAID_FAILED: 'GAMIFICATION',
  CREDITS_LOW: 'SUBSCRIPTION',
  CREDITS_SPENT: 'GAMIFICATION',
  CREDITS_REFUNDED: 'GAMIFICATION',
  STREAK_WARNING: 'GAMIFICATION',
  ACHIEVEMENT_UNLOCKED: 'GAMIFICATION',
  COSMETIC_UNLOCKED: 'GAMIFICATION',
  INVITE_RECEIVED: 'SECURITY',
  MEMBER_ROLE_CHANGED: 'SECURITY',
  BOARD_ROLE_CHANGED: 'SECURITY',
  PERMISSION_CHANGED: 'SECURITY',
  REPORT_READY: 'UPDATES',
  REPORT_FAILED: 'AI_FAILS',
  SAVE_FAILED: 'AI_FAILS',
  SYSTEM_NOTICE: 'UPDATES',
  FORM_REQUESTED: 'UPDATES',
  EDU_ABSENCE: 'EDU_ATTENDANCE',
  EDU_LATE: 'EDU_ATTENDANCE',
  EDU_LEAVE_REQUESTED: 'EDU_LEAVE',
  EDU_LEAVE_DECIDED: 'EDU_LEAVE',
  EDU_HOMEWORK_SET: 'EDU_HOMEWORK',
  EDU_HOMEWORK_SUBMITTED: 'EDU_HOMEWORK',
  EDU_HOMEWORK_RETURNED: 'EDU_HOMEWORK',
  EDU_HOMEWORK_APPROVED: 'EDU_HOMEWORK',
  EDU_HOMEWORK_DUE: 'EDU_HOMEWORK',
  EDU_CLASS_CANCELLED: 'EDU_CLASSES',
  EDU_CLASS_SUBSTITUTE: 'EDU_CLASSES',
  EDU_TODAY_SUMMARY: 'EDU_TODAY',
  EDU_REPORT_CARD_PUBLISHED: 'EDU_REPORTS',
  EDU_FEE_DUE_SOON: 'EDU_FEES',
  EDU_FEE_DUE_TODAY: 'EDU_FEES',
  EDU_FEE_OVERDUE: 'EDU_FEES',
  EDU_FEE_RECEIPT: 'EDU_FEES',
  EDU_BOOKING_STAFF_BOOKED: 'EDU_BOOKINGS',
  EDU_BOOKING_PROMOTED: 'EDU_BOOKINGS',
  EDU_BOOKING_LATE_CANCEL_USED: 'EDU_BOOKINGS',
  EDU_BOOKING_NO_SHOW_USED: 'EDU_BOOKINGS',
  EDU_BOOKING_BLOCKED: 'EDU_BOOKINGS',
  EDU_BOOKING_PACK_EXPIRING: 'EDU_BOOKINGS',
  EDU_BOOKING_PACK_EXPIRED: 'EDU_BOOKINGS',
  EDU_BOOKING_MEMBERSHIP_FROZEN: 'EDU_BOOKINGS',
  EDU_BOOKING_SESSION_CANCELLED: 'EDU_BOOKINGS',
  EDU_CERT_ISSUED: 'EDU_PROGRESS',
  EDU_CERT_EXPIRING: 'EDU_PROGRESS',
  EDU_PRACTICE_COMMENT: 'EDU_PROGRESS',
  EDU_ANNOUNCEMENT_POSTED: 'EDU_ANNOUNCEMENTS',
  EDU_FEED_POSTED: 'EDU_ANNOUNCEMENTS',
  EDU_MESSAGE_RECEIVED: 'EDU_MESSAGES',
  EDU_ENQUIRY_NEW: 'EDU_ADMISSIONS',
  EDU_ENQUIRY_ASSIGNED: 'EDU_ADMISSIONS',
  EDU_ENQUIRY_FOLLOW_UP_DUE: 'EDU_ADMISSIONS',
  EDU_EVENT_PUBLISHED: 'EDU_EVENTS',
  EDU_EVENT_UPDATED: 'EDU_EVENTS',
  EDU_EVENT_CANCELLED: 'EDU_EVENTS',
  EDU_EVENT_REMINDER: 'EDU_EVENTS',
  EDU_PTM_BOOKED: 'EDU_EVENTS',
  EDU_PTM_RESCHEDULED: 'EDU_EVENTS',
  EDU_PTM_CANCELLED: 'EDU_EVENTS',
  EDU_PTM_REMINDER: 'EDU_EVENTS',
  EDU_PTM_SUMMARY_PUBLISHED: 'EDU_EVENTS',
  STOCK_SUGGESTION_WAITING: 'UPDATES',
  STOCK_TRANSFER_ARRIVED: 'UPDATES',
  STOCK_EXPIRING: 'DUE',
  STOCK_LOW: 'DUE',
  HELPER_BLOCKER_RAISED: 'DUE',
  CLIENT_QUOTE_DECIDED: 'UPDATES',
}

export const resolveCategory = (
  eventType: string,
  data?: Record<string, unknown> | null,
): NotificationCategory => {
  if (eventType === 'GROUP_CHAT_MESSAGE') {
    return data && data.isMention ? 'MENTIONS' : 'GROUP_CHAT'
  }
  if (eventType === 'AI_JOB_COMPLETE' || eventType === 'AI_JOB_FAILED') {
    return eventType === 'AI_JOB_FAILED' ? 'AI_FAILS' : 'AI_JOBS'
  }
  if (eventType === 'INGESTION_COMPLETE' || eventType === 'INGESTION_FAILED') {
    return eventType === 'INGESTION_FAILED' ? 'AI_FAILS' : 'INGESTION'
  }
  if (eventType === 'TIP' || eventType === 'PRODUCT_TIP') return 'TIPS'
  if (eventType === 'PRODUCT_UPDATE') return 'UPDATES'
  return EVENT_CATEGORY_MAP[eventType as NotificationEventType] ?? 'UPDATES'
}

export const tierForCategory = (category: NotificationCategory): NotificationTier =>
  HIGH_CATEGORIES.has(category) ? 'HIGH' : 'LOW'

export const isSuppressed = (eventType: string): boolean =>
  SUPPRESSED_EVENT_TYPES.has(eventType.toUpperCase())

export const DEFAULT_CATEGORY_TOGGLES: NotificationCategoryToggles = Object.fromEntries(
  ([
    'MENTIONS', 'DUE', 'AI_FAILS', 'SUBSCRIPTION', 'SECURITY', 'GROUP_CHAT',
    'GAMIFICATION', 'TIPS', 'UPDATES', 'INGESTION', 'AI_JOBS', 'DIGEST',
    'EDU_ATTENDANCE', 'EDU_LEAVE', 'EDU_CLASSES', 'EDU_HOMEWORK', 'EDU_TODAY', 'EDU_REPORTS', 'EDU_FEES', 'EDU_BOOKINGS', 'EDU_PROGRESS',
    'EDU_ANNOUNCEMENTS', 'EDU_MESSAGES', 'EDU_ADMISSIONS', 'EDU_EVENTS',
  ] as NotificationCategory[]).map((category) => {
    const high = HIGH_CATEGORIES.has(category)
    return [category, {
      inApp: true,
      push: high,
      email: category === 'MENTIONS' || category === 'DUE',
      digest: !high && category !== 'DIGEST',
    }]
  }),
) as NotificationCategoryToggles

export const CATEGORY_LABELS: Record<NotificationCategory, string> = {
  MENTIONS: 'Mentions',
  DUE: 'Due dates',
  AI_FAILS: 'AI failures',
  SUBSCRIPTION: 'Subscription alerts',
  SECURITY: 'Security & invites',
  GROUP_CHAT: 'Group chat',
  GAMIFICATION: 'Achievements & credits',
  TIPS: 'Tips',
  UPDATES: 'Product updates',
  INGESTION: 'Ingestion jobs',
  AI_JOBS: 'AI jobs',
  DIGEST: 'Digest',
  EDU_ATTENDANCE: 'Attendance',
  EDU_LEAVE: 'Leave',
  EDU_CLASSES: 'Classes',
  EDU_HOMEWORK: 'Homework',
  EDU_TODAY: 'Today at school',
  EDU_REPORTS: 'Report cards',
  EDU_FEES: 'Fees',
  EDU_BOOKINGS: 'Bookings',
  EDU_PROGRESS: 'Progress',
  EDU_ANNOUNCEMENTS: 'Announcements',
  EDU_MESSAGES: 'Messages',
  EDU_ADMISSIONS: 'Admissions',
  EDU_EVENTS: 'Events',
}

export const CATEGORY_GROUPS: Array<{ label: string; categories: Array<keyof NotificationCategoryToggles> }> = [
  { label: 'High priority', categories: ['MENTIONS', 'DUE', 'AI_FAILS', 'SUBSCRIPTION', 'SECURITY'] },
  { label: 'Digest (batched)', categories: ['GROUP_CHAT', 'GAMIFICATION', 'TIPS', 'UPDATES', 'INGESTION', 'AI_JOBS'] },
]

const padHour = (hour: number) => `${String(Math.max(0, Math.min(23, hour))).padStart(2, '0')}:00`

export const formatQuietRange = (startHour: number, endHour: number): string =>
  `${padHour(startHour)}–${padHour(endHour)}`

export const isWithinQuietWindow = (
  date: Date,
  startHour: number,
  endHour: number,
  timezone: string,
): boolean => {
  const localHourString = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    hour12: false,
    timeZone: timezone || 'UTC',
  }).format(date)
  const localHour = Number(localHourString)
  if (!Number.isFinite(localHour)) return false
  if (startHour === endHour) return false
  if (startHour < endHour) return localHour >= startHour && localHour < endHour
  // Window wraps midnight (e.g. 22→08).
  return localHour >= startHour || localHour < endHour
}
